import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useProjectId } from 'store/project';
import { useInspectionStore } from '../store';
import { CommonTranslationKeys, DialogTranslationKeys } from 'intl/keys';
import { formatTranslation, FormattedMessage } from 'intl';
import { UrlParamName, useAppUrlParams } from 'hooks/navigation/useAppUrlParams';

export interface UseInspectionActionsOptions {
  onAfterArchive?: (inspectionId: string) => void;
  onAfterRestore?: (inspectionId: string) => void;
}

export interface UseInspectionActionsResult {
  archiveInspection: (inspectionId: string) => Promise<void>;
  restoreInspection: (inspectionId: string) => Promise<void>;
}

export function useInspectionActions(
  options: UseInspectionActionsOptions = {}
): UseInspectionActionsResult {
  const { onAfterArchive, onAfterRestore } = options;
  const intl = useIntl();
  const openDialog = useDialog();
  const projectIdFromStore = useProjectId();
  const projectIdFromUrl = useAppUrlParams(UrlParamName.PROJECT_ID);
  const projectId = projectIdFromUrl || projectIdFromStore;
  const archiveInspectionApi = useInspectionStore.use.archiveInspection();
  const restoreInspectionApi = useInspectionStore.use.restoreInspection();

  const archiveInspection = useCallback(
    async (inspectionId: string): Promise<void> => {
      await openDialog({
        catchOnCancel: true,
        rejectOnOffline: true,
        title: (
          <FormattedMessage id={DialogTranslationKeys.confirmationDeleteInspectionTitle} />
        ),
        description: (
          <FormattedMessage
            id={DialogTranslationKeys.confirmationDeleteInspectionDescription}
            values={{ selectedCount: 1 }}
          />
        ),
        customControllerLabels: [
          formatTranslation(intl, CommonTranslationKeys.cancel),
          formatTranslation(intl, CommonTranslationKeys.archive),
        ],
      });

      if (!projectId) {
        throw new Error('Cannot archive inspection: missing projectId');
      }

      await archiveInspectionApi(inspectionId, projectId);
      onAfterArchive?.(inspectionId);
    },
    [intl, openDialog, projectId, archiveInspectionApi, onAfterArchive]
  );

  const restoreInspection = useCallback(
    async (inspectionId: string): Promise<void> => {
      await openDialog({
        catchOnCancel: true,
        rejectOnOffline: true,
        title: (
          <FormattedMessage id={DialogTranslationKeys.confirmationRestoreInspectionTitle} />
        ),
        description: (
          <FormattedMessage
            id={DialogTranslationKeys.confirmationRestoreInspectionDescription}
            values={{ selectedCount: 1 }}
          />
        ),
        customControllerLabels: [
          formatTranslation(intl, CommonTranslationKeys.cancel),
          formatTranslation(intl, CommonTranslationKeys.restore),
        ],
      });

      if (!projectId) {
        throw new Error('Cannot restore inspection: missing projectId');
      }

      await restoreInspectionApi(inspectionId, projectId);
      onAfterRestore?.(inspectionId);
    },
    [intl, openDialog, projectId, restoreInspectionApi, onAfterRestore]
  );

  return {
    archiveInspection,
    restoreInspection,
  };
}
